package com.fantasytenis.web.screens.live

import androidx.compose.runtime.*
import com.fantasytenis.shared.core.realtime.ConnectionState
import com.fantasytenis.shared.core.utils.SharedData
import com.fantasytenis.shared.domain.contests.entity.EntryEntity
import com.fantasytenis.shared.domain.contests.entity.EntryStatus
import com.fantasytenis.shared.domain.lineup.entity.Category
import com.fantasytenis.shared.domain.lineup.entity.LineupEntity
import com.fantasytenis.shared.domain.scoring.entity.LiveScoreEntity
import com.fantasytenis.shared.domain.tennis.entity.MatchEntity
import com.fantasytenis.shared.domain.tennis.entity.PeriodScoreEntity
import com.fantasytenis.web.components.Button
import com.fantasytenis.web.components.ResponsiveHeader
import com.fantasytenis.web.components.Skeleton
import com.fantasytenis.web.components.SkeletonCircle
import com.fantasytenis.web.di.DependencyContainer
import com.fantasytenis.web.hooks.useRealtime
import com.fantasytenis.web.navigation.NavigationState
import com.fantasytenis.web.navigation.Route
import com.fantasytenis.web.theme.Theme
import com.fantasytenis.web.theme.ThemeColors
import com.fantasytenis.web.theme.h2Style
import com.fantasytenis.web.theme.h3Style
import com.fantasytenis.web.theme.textTransform
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Match filter options for the filter chips
 */
enum class MatchFilter(val displayName: String) {
    MY_PLAYERS("My Players"),
    ALL_LIVE("All Live"),
    ATP("ATP"),
    WTA("WTA")
}

/**
 * Live Scoring screen showing user's active entries and live matches.
 * Redesigned to be "fantasy-first" - highlighting user's lineup players.
 */
@Composable
fun LiveScoringScreen() {
    val scope = rememberCoroutineScope()
    val realtimeState = useRealtime()
    val colors = Theme.colors

    // Data states
    var liveMatches by remember { mutableStateOf<List<MatchEntity>>(emptyList()) }
    var upcomingMatches by remember { mutableStateOf<List<MatchEntity>>(emptyList()) }
    var recentlyCompletedMatches by remember { mutableStateOf<List<MatchEntity>>(emptyList()) }
    var userEntries by remember { mutableStateOf<List<EntryEntity>>(emptyList()) }
    var userLineups by remember { mutableStateOf<List<LineupEntity>>(emptyList()) }
    var isLoading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }

    // Filter state
    var selectedFilter by remember { mutableStateOf(MatchFilter.ALL_LIVE) }

    // Observe SharedData for real-time updates
    val liveScores by SharedData.liveMatches.collectAsState()

    // Derive my player IDs from lineups
    val myPlayerIds = remember(userLineups) {
        userLineups.flatMap { it.slots.getAllPlayerIds() }.toSet()
    }

    // Filter matches containing my players
    val myPlayerMatches = remember(liveMatches, myPlayerIds) {
        if (myPlayerIds.isEmpty()) emptyList()
        else liveMatches.filter { match ->
            myPlayerIds.contains(match.player1Id) || myPlayerIds.contains(match.player2Id)
        }
    }

    // Filtered matches based on selected filter
    val filteredMatches = remember(liveMatches, selectedFilter, myPlayerMatches) {
        when (selectedFilter) {
            MatchFilter.MY_PLAYERS -> myPlayerMatches
            MatchFilter.ALL_LIVE -> liveMatches
            MatchFilter.ATP -> liveMatches.filter { it.competition?.contains("ATP", ignoreCase = true) == true }
            MatchFilter.WTA -> liveMatches.filter { it.competition?.contains("WTA", ignoreCase = true) == true }
        }
    }

    // Load initial data
    LaunchedEffect(Unit) {
        isLoading = true
        error = null

        // Load live matches
        DependencyContainer.getLiveMatchesUseCase().fold(
            onSuccess = { matches ->
                liveMatches = matches
            },
            onFailure = { e ->
                error = e.message ?: "Failed to load live matches"
            }
        )

        // Load recently completed matches
        DependencyContainer.getRecentlyCompletedMatchesUseCase().fold(
            onSuccess = { matches ->
                recentlyCompletedMatches = matches
            },
            onFailure = {
                // Non-critical - continue with live matches
            }
        )

        // Load upcoming matches
        DependencyContainer.getUpcomingMatchesUseCase(limit = 10).fold(
            onSuccess = { matches ->
                upcomingMatches = matches
            },
            onFailure = {
                // Non-critical - continue without upcoming
            }
        )

        // Load user entries (non-blocking - user might not have any)
        DependencyContainer.getUserContestEntriesUseCase().fold(
            onSuccess = { entries ->
                userEntries = entries.filter { it.status.isVisibleOnHome }
                SharedData.myActiveEntries.value = userEntries
                console.log("Loaded ${userEntries.size} active entries")
                userEntries.forEach { entry ->
                    console.log("Entry: id=${entry.id}, lineupId=${entry.lineupId}")
                }
            },
            onFailure = { e ->
                console.log("Failed to load entries: ${e.message}")
            }
        )

        // Load user lineups (Phase 2)
        DependencyContainer.getUserLineupsUseCase().fold(
            onSuccess = { lineups ->
                userLineups = lineups
                console.log("Loaded ${lineups.size} lineups")
                lineups.forEach { lineup ->
                    console.log("Lineup: id=${lineup.id}, contestEntryId=${lineup.contestEntryId}, players=${lineup.slots.getAllPlayerIds()}")
                }
            },
            onFailure = { e ->
                console.log("Failed to load lineups: ${e.message}")
            }
        )

        isLoading = false
    }

    // Subscribe to live matches when connected
    LaunchedEffect(realtimeState.isConnected, liveMatches) {
        if (realtimeState.isConnected && liveMatches.isNotEmpty()) {
            liveMatches.forEach { match ->
                DependencyContainer.realtimeConnection.subscribeToMatch(match.id)
            }
        }
    }

    // Cleanup subscriptions when leaving
    DisposableEffect(Unit) {
        onDispose {
            liveMatches.forEach { match ->
                scope.launch {
                    DependencyContainer.realtimeConnection.unsubscribeFromMatch(match.id)
                }
            }
        }
    }

    Div({
        style {
            property("height", "var(--app-height)")
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            backgroundColor(colors.background)
        }
    }) {
        // Header
        ResponsiveHeader(
            title = "Match Center",
            showBackButton = true,
            onBackClick = { NavigationState.navigate(Route.Home) },
            customActions = {
                // Connection status indicator (visible on desktop)
                ConnectionStatusIndicator(realtimeState.isConnected, colors)
            }
        )

        // Main Content - Scrollable area
        Main({
            style {
                flex(1)
                overflowY("auto")
                property("-webkit-overflow-scrolling", "touch")
            }
        }) {
            // Inner container for max-width and padding
            Div({
                style {
                    padding(24.px)
                    maxWidth(1200.px)
                    margin(0.px, "auto".unsafeCast<CSSNumeric>())
                }
            }) {
            when {
                isLoading -> LoadingState(colors)
                error != null -> ErrorState(error!!, colors) {
                    scope.launch {
                        isLoading = true
                        error = null
                        DependencyContainer.getLiveMatchesUseCase().fold(
                            onSuccess = { liveMatches = it; isLoading = false },
                            onFailure = { error = it.message; isLoading = false }
                        )
                    }
                }
                else -> {
                    // User Entries Section (if any) - Phase 6: Expandable
                    if (userEntries.isNotEmpty()) {
                        UserEntriesSection(
                            entries = userEntries,
                            lineups = userLineups,
                            liveScores = liveScores,
                            liveMatches = liveMatches,
                            colors = colors
                        )
                    }

                    // My Players Section (Phase 3) - only show if user has lineup players in live matches
                    if (myPlayerMatches.isNotEmpty()) {
                        MyPlayersSection(
                            matches = myPlayerMatches,
                            myPlayerIds = myPlayerIds,
                            lineups = userLineups,
                            liveScores = liveScores,
                            colors = colors,
                            onMatchClick = { matchId ->
                                NavigationState.navigate(Route.MatchDetail(matchId))
                            }
                        )
                    }

                    // Filter Chips (Phase 7)
                    if (liveMatches.isNotEmpty()) {
                        FilterChips(
                            selectedFilter = selectedFilter,
                            onFilterChange = { selectedFilter = it },
                            myPlayersCount = myPlayerMatches.size,
                            colors = colors
                        )
                    }

                    // Live Matches Section (filtered)
                    LiveMatchesSection(
                        matches = filteredMatches,
                        liveScores = liveScores,
                        myPlayerIds = myPlayerIds,
                        lineups = userLineups,
                        colors = colors,
                        onMatchClick = { matchId ->
                            NavigationState.navigate(Route.MatchDetail(matchId))
                        }
                    )

                    // Up Next Section
                    if (upcomingMatches.isNotEmpty()) {
                        UpNextSection(
                            matches = upcomingMatches,
                            colors = colors,
                            onMatchClick = { matchId ->
                                NavigationState.navigate(Route.MatchDetail(matchId))
                            }
                        )
                    }

                    // Recently Completed Section
                    if (recentlyCompletedMatches.isNotEmpty()) {
                        RecentlyCompletedSection(
                            matches = recentlyCompletedMatches,
                            colors = colors,
                            onMatchClick = { matchId ->
                                NavigationState.navigate(Route.MatchDetail(matchId))
                            }
                        )
                    }
                }
            }
            }
        }
    }
}

@Composable
private fun ConnectionStatusIndicator(isConnected: Boolean, colors: ThemeColors) {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(8.px)
            backgroundColor(rgba(255, 255, 255, 0.1))
            padding(8.px, 12.px)
            borderRadius(20.px)
        }
    }) {
        Span({
            style {
                width(10.px)
                height(10.px)
                borderRadius(50.percent)
                backgroundColor(if (isConnected) colors.success else colors.error)
                if (isConnected) {
                    property("animation", "pulse 2s infinite")
                }
            }
        })
        Span({
            style {
                fontSize(12.px)
                color(colors.textOnPrimary)
                fontWeight(500)
            }
        }) {
            Text(if (isConnected) "Live" else "Connecting...")
        }
    }
}

// ===== Phase 7: Filter Chips =====

@Composable
private fun FilterChips(
    selectedFilter: MatchFilter,
    onFilterChange: (MatchFilter) -> Unit,
    myPlayersCount: Int,
    colors: ThemeColors
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            gap(8.px)
            marginBottom(20.px)
            overflowX("auto")
            property("-webkit-overflow-scrolling", "touch")
            paddingBottom(4.px)
        }
    }) {
        MatchFilter.entries.forEach { filter ->
            val isSelected = filter == selectedFilter
            val chipLabel = when (filter) {
                MatchFilter.MY_PLAYERS -> if (myPlayersCount > 0) "${filter.displayName} ($myPlayersCount)" else filter.displayName
                else -> filter.displayName
            }

            Div({
                style {
                    padding(8.px, 16.px)
                    borderRadius(20.px)
                    fontSize(13.px)
                    fontWeight(if (isSelected) 600 else 500)
                    cursor("pointer")
                    property("white-space", "nowrap")
                    property("transition", "all 0.2s")

                    if (isSelected) {
                        backgroundColor(colors.primary)
                        color(colors.textOnPrimary)
                    } else {
                        backgroundColor(colors.surfaceVariant)
                        color(colors.textSecondary)
                    }
                }
                onClick { onFilterChange(filter) }
            }) {
                Text(chipLabel)
            }
        }
    }
}

// ===== Phase 3: My Players Section =====

@Composable
private fun MyPlayersSection(
    matches: List<MatchEntity>,
    myPlayerIds: Set<String>,
    lineups: List<LineupEntity>,
    liveScores: Map<String, LiveScoreEntity>,
    colors: ThemeColors,
    onMatchClick: (String) -> Unit
) {
    Section({
        style {
            marginBottom(32.px)
        }
    }) {
        // Header
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
                marginBottom(16.px)
            }
        }) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(8.px)
                }
            }) {
                Span({
                    style {
                        fontSize(18.px)
                    }
                }) {
                    Text("\u2B50") // Star emoji
                }
                H3({
                    style {
                        h3Style()
                        color(colors.textPrimary)
                    }
                }) {
                    Text("My Players")
                }
            }
            Span({
                style {
                    fontSize(13.px)
                    fontWeight(500)
                    color(colors.textOnPrimary)
                    backgroundColor(colors.primary)
                    padding(4.px, 12.px)
                    borderRadius(12.px)
                }
            }) {
                Text("${matches.size} active")
            }
        }

        // Match cards grid
        Div({
            style {
                display(DisplayStyle.Grid)
                property("grid-template-columns", "repeat(auto-fill, minmax(min(100%, 320px), 1fr))")
                gap(20.px)
            }
        }) {
            matches.forEach { match ->
                MyPlayerMatchCard(
                    match = match,
                    myPlayerIds = myPlayerIds,
                    lineups = lineups,
                    liveScore = liveScores[match.id],
                    colors = colors,
                    onClick = { onMatchClick(match.id) }
                )
            }
        }
    }
}

// ===== Phase 4: MyPlayerMatchCard =====

@Composable
private fun MyPlayerMatchCard(
    match: MatchEntity,
    myPlayerIds: Set<String>,
    lineups: List<LineupEntity>,
    liveScore: LiveScoreEntity?,
    colors: ThemeColors,
    onClick: () -> Unit
) {
    val player1IsMine = myPlayerIds.contains(match.player1Id)
    val player2IsMine = myPlayerIds.contains(match.player2Id)

    // Find category for my players
    val player1Category = if (player1IsMine) {
        lineups.firstNotNullOfOrNull { it.getCategoryForPlayer(match.player1Id) }
    } else null

    val player2Category = if (player2IsMine) {
        lineups.firstNotNullOfOrNull { it.getCategoryForPlayer(match.player2Id) }
    } else null

    Div({
        classes("baseline-card")
        style {
            backgroundColor(colors.surface)
            borderRadius(12.px)
            padding(20.px)
            cursor("pointer")
            property("box-shadow", "0 2px 8px rgba(0, 0, 0, 0.1)")
            property("transition", "transform 0.2s, box-shadow 0.2s")
            // Green border highlight for my player matches
            property("border", "2px solid ${colors.success}")
        }
        onClick { onClick() }
    }) {
        // Header with Live badge and "Your Player" indicator
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
                marginBottom(12.px)
            }
        }) {
            LiveBadge(colors)
            Span({
                style {
                    fontSize(11.px)
                    fontWeight(600)
                    color(colors.success)
                    backgroundColor(rgba(76, 175, 80, 0.15))
                    padding(4.px, 8.px)
                    borderRadius(4.px)
                }
            }) {
                Text("\u2B50 Your Player")
            }
        }

        // Match info header
        if (match.round != null || match.court != null) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    gap(8.px)
                    marginBottom(12.px)
                }
            }) {
                match.round?.let { round ->
                    Span({
                        style {
                            fontSize(12.px)
                            color(colors.textSecondary)
                            backgroundColor(colors.surfaceVariant)
                            padding(4.px, 8.px)
                            borderRadius(4.px)
                        }
                    }) {
                        Text(round)
                    }
                }
                match.court?.let { court ->
                    Span({
                        style {
                            fontSize(12.px)
                            color(colors.textSecondary)
                        }
                    }) {
                        Text(court)
                    }
                }
            }
        }

        // Player 1 score row (Phase 5: Enhanced)
        EnhancedPlayerRow(
            playerName = liveScore?.player1?.playerName ?: match.player1Name ?: "Player 1",
            isMine = player1IsMine,
            category = player1Category,
            sets = liveScore?.player1?.sets
                ?: extractCompletedSetScores(match.periodScores, isPlayer1 = true),
            games = liveScore?.player1?.games
                ?: getCurrentSetGames(match.periodScores, isPlayer1 = true),
            fantasyPoints = liveScore?.fantasyPoints?.get(match.player1Id),
            isServing = liveScore?.serving == "player1",
            colors = colors
        )

        // Divider
        Div({
            style {
                height(1.px)
                backgroundColor(colors.border)
                margin(12.px, 0.px)
            }
        })

        // Player 2 score row (Phase 5: Enhanced)
        EnhancedPlayerRow(
            playerName = liveScore?.player2?.playerName ?: match.player2Name ?: "Player 2",
            isMine = player2IsMine,
            category = player2Category,
            sets = liveScore?.player2?.sets
                ?: extractCompletedSetScores(match.periodScores, isPlayer1 = false),
            games = liveScore?.player2?.games
                ?: getCurrentSetGames(match.periodScores, isPlayer1 = false),
            fantasyPoints = liveScore?.fantasyPoints?.get(match.player2Id),
            isServing = liveScore?.serving == "player2",
            colors = colors
        )
    }
}

// ===== Phase 5: EnhancedPlayerRow =====

@Composable
private fun EnhancedPlayerRow(
    playerName: String,
    isMine: Boolean,
    category: Category?,
    sets: List<Int>,
    games: Int,
    fantasyPoints: Double?,
    isServing: Boolean,
    colors: ThemeColors
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.SpaceBetween)
            alignItems(AlignItems.Center)
            if (isMine) {
                backgroundColor(rgba(76, 175, 80, 0.08))
                margin((-4).px)
                padding(4.px)
                borderRadius(6.px)
            }
        }
    }) {
        // Player name with star/serving indicator and category badge
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(6.px)
                flex(1)
            }
        }) {
            // Star or serving indicator
            if (isMine) {
                Span({
                    style {
                        fontSize(12.px)
                    }
                }) {
                    Text("\u2B50")
                }
            } else if (isServing) {
                Span({
                    style {
                        width(8.px)
                        height(8.px)
                        borderRadius(50.percent)
                        backgroundColor(colors.secondary)
                    }
                })
            } else {
                Span({ style { width(12.px) } })
            }

            // Player name
            Span({
                style {
                    fontSize(14.px)
                    fontWeight(if (isMine || isServing) 600 else 400)
                    color(colors.textPrimary)
                    property("white-space", "nowrap")
                    overflow("hidden")
                    property("text-overflow", "ellipsis")
                    maxWidth(100.px)
                }
            }) {
                Text(playerName)
            }

            // Category badge (if my player)
            category?.let { cat ->
                Span({
                    style {
                        fontSize(9.px)
                        fontWeight(700)
                        color(colors.primary)
                        backgroundColor(colors.primaryLight)
                        padding(2.px, 6.px)
                        borderRadius(3.px)
                        textTransform("uppercase")
                    }
                }) {
                    Text(cat.displayName.take(3))
                }
            }
        }

        // Sets and games
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(6.px)
            }
        }) {
            sets.forEach { setScore ->
                Span({
                    style {
                        width(20.px)
                        textAlign("center")
                        fontSize(13.px)
                        fontWeight(600)
                        color(colors.textPrimary)
                    }
                }) {
                    Text(setScore.toString())
                }
            }

            Span({
                style {
                    width(22.px)
                    textAlign("center")
                    fontSize(13.px)
                    fontWeight(500)
                    color(colors.textSecondary)
                    backgroundColor(colors.surfaceVariant)
                    padding(2.px, 4.px)
                    borderRadius(4.px)
                }
            }) {
                Text(games.toString())
            }
        }

        // Fantasy points (prominent display)
        fantasyPoints?.let { pts ->
            val isPositive = pts >= 0
            Span({
                style {
                    marginLeft(8.px)
                    fontSize(12.px)
                    fontWeight(700)
                    color(if (isPositive) colors.success else colors.error)
                    backgroundColor(if (isPositive) rgba(76, 175, 80, 0.15) else rgba(244, 67, 54, 0.15))
                    padding(4.px, 8.px)
                    borderRadius(4.px)
                }
            }) {
                Text("${if (isPositive) "+" else ""}${pts.toInt()} FP")
            }
        }
    }
}

// ===== Phase 6: Expandable User Entries Section =====

@Composable
private fun UserEntriesSection(
    entries: List<EntryEntity>,
    lineups: List<LineupEntity>,
    liveScores: Map<String, LiveScoreEntity>,
    liveMatches: List<MatchEntity>,
    colors: ThemeColors
) {
    Section({
        style {
            marginBottom(32.px)
        }
    }) {
        H3({
            style {
                h3Style()
                marginBottom(16.px)
                color(colors.textPrimary)
            }
        }) {
            Text("Your Active Entries")
        }

        Div({
            style {
                display(DisplayStyle.Grid)
                property("grid-template-columns", "repeat(auto-fill, minmax(min(100%, 300px), 1fr))")
                gap(16.px)
            }
        }) {
            entries.forEach { entry ->
                // Find matching lineup for this entry (match by lineupId or contestEntryId)
                val lineup = lineups.find {
                    it.id == entry.lineupId || it.contestEntryId == entry.id
                }
                ExpandableUserEntryCard(
                    entry = entry,
                    lineup = lineup,
                    liveScores = liveScores,
                    liveMatches = liveMatches,
                    colors = colors,
                    onViewLeaderboard = {
                        NavigationState.navigate(Route.ContestLeaderboard(entry.contestId))
                    }
                )
            }
        }
    }
}

@Composable
private fun ExpandableUserEntryCard(
    entry: EntryEntity,
    lineup: LineupEntity?,
    liveScores: Map<String, LiveScoreEntity>,
    liveMatches: List<MatchEntity>,
    colors: ThemeColors,
    onViewLeaderboard: () -> Unit
) {
    var isExpanded by remember { mutableStateOf(false) }

    Div({
        classes("baseline-card")
        style {
            backgroundColor(colors.primary)
            backgroundImage("linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)")
            borderRadius(12.px)
            padding(20.px)
            cursor("pointer")
            property("transition", "transform 0.2s")
        }
    }) {
        // Main card content (clickable for leaderboard)
        Div({
            onClick { onViewLeaderboard() }
        }) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.SpaceBetween)
                    alignItems(AlignItems.FlexStart)
                    marginBottom(12.px)
                }
            }) {
                Span({
                    style {
                        fontSize(14.px)
                        fontWeight(500)
                        color(rgba(255, 255, 255, 0.8))
                    }
                }) {
                    Text("Entry #${entry.entryNumber}")
                }
                entry.rank?.let { rank ->
                    Span({
                        style {
                            backgroundColor(rgba(255, 255, 255, 0.2))
                            padding(4.px, 10.px)
                            borderRadius(12.px)
                            fontSize(12.px)
                            fontWeight(600)
                            color(colors.textOnPrimary)
                        }
                    }) {
                        Text("#$rank")
                    }
                }
            }

            Div({
                style {
                    fontSize(32.px)
                    fontWeight(700)
                    color(colors.textOnPrimary)
                    marginBottom(8.px)
                }
            }) {
                Text("${entry.totalPoints.toInt()} pts")
            }

            if (entry.payoutAmount > 0) {
                Div({
                    style {
                        fontSize(14.px)
                        color(rgba(255, 255, 255, 0.9))
                        marginBottom(12.px)
                    }
                }) {
                    Text("Projected: $${entry.payoutAmount.asDynamic().toFixed(2)}")
                }
            }
        }

        // Expand/Collapse button
        if (lineup != null) {
            Div({
                style {
                    property("border-top", "1px solid rgba(255, 255, 255, 0.2)")
                    paddingTop(12.px)
                    marginTop(8.px)
                }
                onClick { isExpanded = !isExpanded }
            }) {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        justifyContent(JustifyContent.Center)
                        gap(6.px)
                        fontSize(13.px)
                        fontWeight(500)
                        color(rgba(255, 255, 255, 0.9))
                    }
                }) {
                    Text(if (isExpanded) "\u25B2 Hide Lineup" else "\u25BC View Lineup")
                }

                // Expanded lineup content
                if (isExpanded) {
                    Div({
                        style {
                            marginTop(16.px)
                            display(DisplayStyle.Flex)
                            flexDirection(FlexDirection.Column)
                            gap(8.px)
                        }
                    }) {
                        Category.entries.forEach { category ->
                            val playerId = lineup.slots.getPlayerForCategory(category)
                            if (playerId != null) {
                                // Check if player is currently live
                                val isLive = liveMatches.any { match ->
                                    match.player1Id == playerId || match.player2Id == playerId
                                }
                                // Get fantasy points from live scores
                                val fantasyPts = liveScores.values.firstNotNullOfOrNull {
                                    it.fantasyPoints[playerId]
                                }

                                LineupSlotRow(
                                    category = category,
                                    playerId = playerId,
                                    isLive = isLive,
                                    fantasyPoints = fantasyPts,
                                    colors = colors
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun LineupSlotRow(
    category: Category,
    playerId: String,
    isLive: Boolean,
    fantasyPoints: Double?,
    colors: ThemeColors
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.SpaceBetween)
            alignItems(AlignItems.Center)
            backgroundColor(rgba(255, 255, 255, 0.1))
            padding(8.px, 12.px)
            borderRadius(6.px)
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(8.px)
            }
        }) {
            // Category icon/badge
            Span({
                style {
                    fontSize(10.px)
                    fontWeight(700)
                    color(colors.textOnPrimary)
                    backgroundColor(rgba(255, 255, 255, 0.2))
                    padding(2.px, 6.px)
                    borderRadius(3.px)
                }
            }) {
                Text(category.displayName.take(3).uppercase())
            }

            // Player ID (would ideally be player name)
            Span({
                style {
                    fontSize(13.px)
                    color(colors.textOnPrimary)
                    maxWidth(120.px)
                    overflow("hidden")
                    property("text-overflow", "ellipsis")
                    property("white-space", "nowrap")
                }
            }) {
                Text(playerId.take(12) + if (playerId.length > 12) "..." else "")
            }
        }

        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(8.px)
            }
        }) {
            // Live indicator
            if (isLive) {
                Span({
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        gap(4.px)
                        fontSize(10.px)
                        fontWeight(600)
                        color(colors.error)
                    }
                }) {
                    Span({
                        style {
                            width(6.px)
                            height(6.px)
                            borderRadius(50.percent)
                            backgroundColor(colors.error)
                            property("animation", "pulse 1.5s infinite")
                        }
                    })
                    Text("LIVE")
                }
            }

            // Fantasy points
            fantasyPoints?.let { pts ->
                Span({
                    style {
                        fontSize(12.px)
                        fontWeight(600)
                        color(colors.textOnPrimary)
                    }
                }) {
                    Text("${if (pts >= 0) "+" else ""}${pts.toInt()} FP")
                }
            }
        }
    }
}

// ===== Live Matches Section (updated to support my players highlight) =====

@Composable
private fun LiveMatchesSection(
    matches: List<MatchEntity>,
    liveScores: Map<String, LiveScoreEntity>,
    myPlayerIds: Set<String>,
    lineups: List<LineupEntity>,
    colors: ThemeColors,
    onMatchClick: (String) -> Unit
) {
    Section {
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
                marginBottom(16.px)
            }
        }) {
            H3({
                style {
                    h3Style()
                    color(colors.textPrimary)
                }
            }) {
                Text("Live Matches")
            }
            if (matches.isNotEmpty()) {
                Span({
                    style {
                        fontSize(14.px)
                        color(colors.textSecondary)
                    }
                }) {
                    Text("${matches.size} match${if (matches.size != 1) "es" else ""}")
                }
            }
        }

        if (matches.isEmpty()) {
            EmptyMatchesState(colors)
        } else {
            Div({
                style {
                    display(DisplayStyle.Grid)
                    property("grid-template-columns", "repeat(auto-fill, minmax(min(100%, 320px), 1fr))")
                    gap(20.px)
                }
            }) {
                matches.forEach { match ->
                    val hasMyPlayer = myPlayerIds.contains(match.player1Id) || myPlayerIds.contains(match.player2Id)

                    if (hasMyPlayer) {
                        MyPlayerMatchCard(
                            match = match,
                            myPlayerIds = myPlayerIds,
                            lineups = lineups,
                            liveScore = liveScores[match.id],
                            colors = colors,
                            onClick = { onMatchClick(match.id) }
                        )
                    } else {
                        LiveMatchCard(
                            match = match,
                            liveScore = liveScores[match.id],
                            colors = colors,
                            onClick = { onMatchClick(match.id) }
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun LiveMatchCard(
    match: MatchEntity,
    liveScore: LiveScoreEntity?,
    colors: ThemeColors,
    onClick: () -> Unit
) {
    Div({
        classes("baseline-card")
        style {
            backgroundColor(colors.surface)
            borderRadius(12.px)
            padding(20.px)
            cursor("pointer")
            property("box-shadow", "0 2px 8px rgba(0, 0, 0, 0.1)")
            property("transition", "transform 0.2s, box-shadow 0.2s")
        }
        onClick { onClick() }
    }) {
        // Live badge
        LiveBadge(colors)

        // Match info header
        if (match.round != null || match.court != null) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    gap(8.px)
                    marginBottom(12.px)
                }
            }) {
                match.round?.let { round ->
                    Span({
                        style {
                            fontSize(12.px)
                            color(colors.textSecondary)
                            backgroundColor(colors.surfaceVariant)
                            padding(4.px, 8.px)
                            borderRadius(4.px)
                        }
                    }) {
                        Text(round)
                    }
                }
                match.court?.let { court ->
                    Span({
                        style {
                            fontSize(12.px)
                            color(colors.textSecondary)
                        }
                    }) {
                        Text(court)
                    }
                }
            }
        }

        // Player 1 score row
        PlayerScoreRow(
            playerName = liveScore?.player1?.playerName ?: match.player1Name ?: "Player 1",
            sets = liveScore?.player1?.sets
                ?: extractCompletedSetScores(match.periodScores, isPlayer1 = true),
            games = liveScore?.player1?.games
                ?: getCurrentSetGames(match.periodScores, isPlayer1 = true),
            points = liveScore?.player1?.points ?: "-",
            isServing = liveScore?.serving == "player1",
            fantasyPoints = liveScore?.fantasyPoints?.get(match.player1Id),
            colors = colors
        )

        // Divider
        Div({
            style {
                height(1.px)
                backgroundColor(colors.border)
                margin(12.px, 0.px)
            }
        })

        // Player 2 score row
        PlayerScoreRow(
            playerName = liveScore?.player2?.playerName ?: match.player2Name ?: "Player 2",
            sets = liveScore?.player2?.sets
                ?: extractCompletedSetScores(match.periodScores, isPlayer1 = false),
            games = liveScore?.player2?.games
                ?: getCurrentSetGames(match.periodScores, isPlayer1 = false),
            points = liveScore?.player2?.points ?: "-",
            isServing = liveScore?.serving == "player2",
            fantasyPoints = liveScore?.fantasyPoints?.get(match.player2Id),
            colors = colors
        )

        // Static score display if no live data
        if (liveScore == null && match.score != null) {
            Div({
                style {
                    marginTop(12.px)
                    fontSize(14.px)
                    color(colors.textSecondary)
                    textAlign("center")
                }
            }) {
                Text(match.score!!)
            }
        }
    }
}

@Composable
private fun LiveBadge(colors: ThemeColors) {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(6.px)
            marginBottom(12.px)
        }
    }) {
        Span({
            style {
                width(8.px)
                height(8.px)
                borderRadius(50.percent)
                backgroundColor(colors.error)
                property("animation", "pulse 1.5s infinite")
            }
        })
        Span({
            style {
                fontSize(11.px)
                fontWeight(700)
                color(colors.error)
                textTransform("uppercase")
                letterSpacing(0.5.px)
            }
        }) {
            Text("LIVE")
        }
    }
}

@Composable
private fun UpNextSection(
    matches: List<MatchEntity>,
    colors: ThemeColors,
    onMatchClick: (String) -> Unit
) {
    Section({
        style {
            marginTop(40.px)
        }
    }) {
        // Header with clock icon
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
                marginBottom(16.px)
            }
        }) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(8.px)
                }
            }) {
                Span({
                    style {
                        fontSize(16.px)
                    }
                }) {
                    Text("\u23F1") // Stopwatch emoji
                }
                H3({
                    style {
                        h3Style()
                        color(colors.textPrimary)
                    }
                }) {
                    Text("Up Next")
                }
            }
            Span({
                style {
                    fontSize(14.px)
                    color(colors.textSecondary)
                }
            }) {
                Text("${matches.size} match${if (matches.size != 1) "es" else ""}")
            }
        }

        // Match grid
        Div({
            style {
                display(DisplayStyle.Grid)
                property("grid-template-columns", "repeat(auto-fill, minmax(min(100%, 320px), 1fr))")
                gap(20.px)
            }
        }) {
            matches.forEach { match ->
                UpcomingMatchCard(
                    match = match,
                    colors = colors,
                    onClick = { onMatchClick(match.id) }
                )
            }
        }
    }
}

@Composable
private fun UpcomingMatchCard(
    match: MatchEntity,
    colors: ThemeColors,
    onClick: () -> Unit
) {
    val countdown = remember(match.scheduledTime) {
        calculateCountdown(match.scheduledTime)
    }

    Div({
        classes("baseline-card")
        style {
            backgroundColor(colors.surface)
            borderRadius(12.px)
            padding(20.px)
            cursor("pointer")
            property("box-shadow", "0 2px 8px rgba(0, 0, 0, 0.1)")
            property("transition", "transform 0.2s, box-shadow 0.2s")
        }
        onClick { onClick() }
    }) {
        // Countdown badge with times
        UpcomingBadgeWithTimes(countdown, match.scheduledTime, match.competition, colors)

        // Competition & round
        Div({
            style {
                display(DisplayStyle.Flex)
                gap(8.px)
                marginBottom(12.px)
                flexWrap(FlexWrap.Wrap)
            }
        }) {
            match.competition?.let { competition ->
                Span({
                    style {
                        fontSize(12.px)
                        color(colors.textSecondary)
                        backgroundColor(colors.surfaceVariant)
                        padding(4.px, 8.px)
                        borderRadius(4.px)
                    }
                }) {
                    Text(competition)
                }
            }
            match.round?.let { round ->
                Span({
                    style {
                        fontSize(12.px)
                        color(colors.textSecondary)
                    }
                }) {
                    Text(round)
                }
            }
        }

        // Player 1
        UpcomingPlayerRow(match.player1Name ?: "TBD", colors)

        // VS divider
        Div({
            style {
                textAlign("center")
                fontSize(12.px)
                color(colors.textSecondary)
                margin(8.px, 0.px)
            }
        }) {
            Text("vs")
        }

        // Player 2
        UpcomingPlayerRow(match.player2Name ?: "TBD", colors)

        // Court (if available)
        match.court?.let { court ->
            Div({
                style {
                    marginTop(12.px)
                    fontSize(12.px)
                    color(colors.textSecondary)
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(4.px)
                }
            }) {
                Span {
                    Text("\uD83D\uDCCD") // Pin emoji
                }
                Text(court)
            }
        }
    }
}

@Composable
private fun UpcomingBadge(countdown: String, colors: ThemeColors) {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(6.px)
            marginBottom(12.px)
        }
    }) {
        Span({
            style {
                fontSize(12.px)
            }
        }) {
            Text("\u23F1") // Stopwatch emoji
        }
        Span({
            style {
                fontSize(11.px)
                fontWeight(700)
                color(colors.primary)
                textTransform("uppercase")
                letterSpacing(0.5.px)
            }
        }) {
            Text(countdown)
        }
    }
}

@Composable
private fun UpcomingPlayerRow(playerName: String, colors: ThemeColors) {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(8.px)
        }
    }) {
        Span({
            style {
                width(22.px)
            }
        }) // Spacer for alignment
        Span({
            style {
                fontSize(14.px)
                fontWeight(500)
                color(colors.textPrimary)
                property("white-space", "nowrap")
                overflow("hidden")
                property("text-overflow", "ellipsis")
            }
        }) {
            Text(playerName)
        }
    }
}

private fun calculateCountdown(scheduledTime: String): String {
    return try {
        val scheduled = kotlin.js.Date(scheduledTime)
        val now = kotlin.js.Date()
        val diffMs = scheduled.getTime() - now.getTime()

        if (diffMs <= 0) return "Starting soon"

        val diffMinutes = (diffMs / 60000).toInt()
        val diffHours = diffMinutes / 60
        val remainingMinutes = diffMinutes % 60

        when {
            diffMinutes < 60 -> "Starts in $diffMinutes min"
            diffHours < 24 -> {
                if (remainingMinutes > 0) "Starts in ${diffHours}h ${remainingMinutes}m"
                else "Starts in ${diffHours}h"
            }
            else -> {
                "Tomorrow"
            }
        }
    } catch (e: Exception) {
        "Scheduled"
    }
}

/**
 * Format a time for display (e.g., "4:55 PM")
 */
private fun formatTimeForDisplay(hours: Int, minutes: Int): String {
    val amPm = if (hours >= 12) "PM" else "AM"
    val hour12 = if (hours % 12 == 0) 12 else hours % 12
    val minStr = minutes.toString().padStart(2, '0')
    return "$hour12:$minStr $amPm"
}

/**
 * Get venue timezone info based on competition name
 * Returns array of [UTC offset hours, timezone abbreviation, location name]
 */
private fun getVenueTimezone(competition: String?): Array<Any>? {
    if (competition == null) return null

    val compLower = competition.lowercase()
    return when {
        // Australian tournaments (AEDT = UTC+11 during summer)
        compLower.contains("australian") -> arrayOf(11.0, "AEDT", "Melbourne")

        // French tournaments (CEST = UTC+2 during summer, CET = UTC+1 during winter)
        compLower.contains("roland garros") || compLower.contains("french open") ->
            arrayOf(2.0, "CEST", "Paris")

        // UK tournaments (BST = UTC+1 during summer)
        compLower.contains("wimbledon") -> arrayOf(1.0, "BST", "London")

        // US tournaments (EDT = UTC-4 during summer)
        compLower.contains("us open") -> arrayOf(-4.0, "EDT", "New York")
        compLower.contains("indian wells") || compLower.contains("bnp paribas") ->
            arrayOf(-7.0, "PDT", "California")
        compLower.contains("miami") -> arrayOf(-4.0, "EDT", "Miami")

        // ATP Masters 1000
        compLower.contains("monte carlo") || compLower.contains("monte-carlo") ->
            arrayOf(2.0, "CEST", "Monaco")
        compLower.contains("madrid") -> arrayOf(2.0, "CEST", "Madrid")
        compLower.contains("rome") || compLower.contains("italian") ->
            arrayOf(2.0, "CEST", "Rome")
        compLower.contains("canada") || compLower.contains("rogers cup") ->
            arrayOf(-4.0, "EDT", "Canada")
        compLower.contains("cincinnati") -> arrayOf(-4.0, "EDT", "Cincinnati")
        compLower.contains("shanghai") -> arrayOf(8.0, "CST", "Shanghai")
        compLower.contains("paris") && !compLower.contains("roland") ->
            arrayOf(1.0, "CET", "Paris")

        // ATP Finals
        compLower.contains("atp finals") || compLower.contains("tour finals") ->
            arrayOf(1.0, "CET", "Turin")

        else -> null
    }
}

/**
 * Enhanced upcoming badge that shows countdown and actual times
 * Note: @NoLiveLiterals needed due to complex nested try-catch with elvis operators
 */
@NoLiveLiterals
@Composable
private fun UpcomingBadgeWithTimes(
    countdown: String,
    scheduledTime: String,
    competition: String?,
    colors: ThemeColors
) {
    // Calculate times inside composable
    val scheduled = try { kotlin.js.Date(scheduledTime) } catch (e: Exception) { null }

    val localTime = scheduled?.let {
        formatTimeForDisplay(it.getHours(), it.getMinutes())
    } ?: "TBD"

    val localTimezone = scheduled?.let {
        try {
            val options = js("{}")
            options["timeZoneName"] = "short"
            val formatter = it.toLocaleTimeString("en-US", options)
            formatter.split(" ").lastOrNull() ?: ""
        } catch (e: Exception) { "" }
    } ?: ""

    // Calculate venue time if different
    val venueInfo = getVenueTimezone(competition)
    val venueOffset = venueInfo?.get(0) as? Double
    val venueTzAbbrev = venueInfo?.get(1) as? String
    val venueLocation = venueInfo?.get(2) as? String

    val userOffsetHours = scheduled?.let { -it.getTimezoneOffset() / 60.0 } ?: 0.0
    val showVenueTime = venueOffset != null && kotlin.math.abs(userOffsetHours - venueOffset) >= 0.5

    val venueTime = if (showVenueTime && scheduled != null && venueOffset != null) {
        val utcMs = scheduled.getTime()
        val localOffsetMs = scheduled.getTimezoneOffset() * 60 * 1000
        val venueMs = utcMs + (venueOffset * 60 * 60 * 1000)
        val venueDate = kotlin.js.Date(venueMs + localOffsetMs)
        formatTimeForDisplay(venueDate.getHours(), venueDate.getMinutes())
    } else null

    Div({
        style {
            marginBottom(12.px)
        }
    }) {
        // Main countdown row
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(6.px)
                marginBottom(4.px)
            }
        }) {
            Span({
                style {
                    fontSize(12.px)
                }
            }) {
                Text("\u23F1") // Stopwatch emoji
            }
            Span({
                style {
                    fontSize(11.px)
                    fontWeight(700)
                    color(colors.primary)
                    textTransform("uppercase")
                    letterSpacing(0.5.px)
                }
            }) {
                Text(countdown)
            }
        }

        // Time display row
        Div({
            style {
                display(DisplayStyle.Flex)
                flexWrap(FlexWrap.Wrap)
                gap(8.px)
                alignItems(AlignItems.Center)
            }
        }) {
            // Local time (your time)
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(4.px)
                }
            }) {
                Span({
                    style {
                        fontSize(11.px)
                        color(colors.textSecondary)
                    }
                }) {
                    Text("\uD83D\uDCCD") // Pin/location emoji for "Your time"
                }
                Span({
                    style {
                        fontSize(12.px)
                        fontWeight(500)
                        color(colors.textPrimary)
                    }
                }) {
                    Text(localTime)
                }
                if (localTimezone.isNotEmpty()) {
                    Span({
                        style {
                            fontSize(10.px)
                            color(colors.textSecondary)
                        }
                    }) {
                        Text(localTimezone)
                    }
                }
            }

            // Venue time (if different from local)
            if (showVenueTime && venueTime != null) {
                // Separator
                Span({
                    style {
                        fontSize(10.px)
                        color(colors.textSecondary)
                    }
                }) {
                    Text("\u2022") // Bullet point
                }

                // Venue time
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        gap(4.px)
                    }
                }) {
                    Span({
                        style {
                            fontSize(11.px)
                            color(colors.textSecondary)
                        }
                    }) {
                        Text("\uD83C\uDFBE") // Tennis ball emoji for venue
                    }
                    Span({
                        style {
                            fontSize(12.px)
                            fontWeight(500)
                            color(colors.textSecondary)
                        }
                    }) {
                        Text(venueTime)
                    }
                    Span({
                        style {
                            fontSize(10.px)
                            color(colors.textSecondary)
                        }
                    }) {
                        val tzLabel = if (venueLocation != null) {
                            "${venueTzAbbrev ?: ""} ($venueLocation)"
                        } else {
                            venueTzAbbrev ?: ""
                        }
                        Text(tzLabel)
                    }
                }
            }
        }
    }
}

@Composable
private fun RecentlyCompletedSection(
    matches: List<MatchEntity>,
    colors: ThemeColors,
    onMatchClick: (String) -> Unit
) {
    Section({
        style {
            marginTop(40.px)
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
                marginBottom(16.px)
            }
        }) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(8.px)
                }
            }) {
                Span({
                    style {
                        fontSize(16.px)
                        color(colors.textSecondary)
                    }
                }) {
                    Text("\u2713") // Checkmark
                }
                H3({
                    style {
                        h3Style()
                        color(colors.textPrimary)
                    }
                }) {
                    Text("Recently Completed")
                }
            }
            Span({
                style {
                    fontSize(14.px)
                    color(colors.textSecondary)
                }
            }) {
                Text("${matches.size} match${if (matches.size != 1) "es" else ""}")
            }
        }

        Div({
            style {
                display(DisplayStyle.Grid)
                property("grid-template-columns", "repeat(auto-fill, minmax(min(100%, 320px), 1fr))")
                gap(20.px)
            }
        }) {
            matches.forEach { match ->
                CompletedMatchCard(
                    match = match,
                    colors = colors,
                    onClick = { onMatchClick(match.id) }
                )
            }
        }
    }
}

@Composable
private fun CompletedMatchCard(
    match: MatchEntity,
    colors: ThemeColors,
    onClick: () -> Unit
) {
    // Determine winner based on score or winnerId
    val player1IsWinner = match.winnerId == match.player1Id ||
        (match.score?.let { score ->
            val parts = score.split("-")
            if (parts.size == 2) {
                val p1Sets = parts[0].trim().toIntOrNull() ?: 0
                val p2Sets = parts[1].trim().toIntOrNull() ?: 0
                p1Sets > p2Sets
            } else false
        } ?: false)

    val player2IsWinner = match.winnerId == match.player2Id ||
        (match.score?.let { score ->
            val parts = score.split("-")
            if (parts.size == 2) {
                val p1Sets = parts[0].trim().toIntOrNull() ?: 0
                val p2Sets = parts[1].trim().toIntOrNull() ?: 0
                p2Sets > p1Sets
            } else false
        } ?: false)

    Div({
        classes("baseline-card")
        style {
            backgroundColor(colors.surface)
            borderRadius(12.px)
            padding(20.px)
            cursor("pointer")
            property("box-shadow", "0 2px 8px rgba(0, 0, 0, 0.1)")
            property("transition", "transform 0.2s, box-shadow 0.2s")
        }
        onClick { onClick() }
    }) {
        // FINAL badge
        FinalBadge(colors)

        // Match info header (competition + round)
        Div({
            style {
                display(DisplayStyle.Flex)
                gap(8.px)
                marginBottom(12.px)
                flexWrap(FlexWrap.Wrap)
            }
        }) {
            match.competition?.let { competition ->
                Span({
                    style {
                        fontSize(12.px)
                        color(colors.textSecondary)
                        backgroundColor(colors.surfaceVariant)
                        padding(4.px, 8.px)
                        borderRadius(4.px)
                    }
                }) {
                    Text(competition)
                }
            }
            match.round?.let { round ->
                Span({
                    style {
                        fontSize(12.px)
                        color(colors.textSecondary)
                    }
                }) {
                    Text(round)
                }
            }
        }

        // Player 1 row
        CompletedPlayerRow(
            playerName = match.player1Name ?: "Player 1",
            isWinner = player1IsWinner,
            score = match.score,
            colors = colors
        )

        // Divider
        Div({
            style {
                height(1.px)
                backgroundColor(colors.border)
                margin(12.px, 0.px)
            }
        })

        // Player 2 row
        CompletedPlayerRow(
            playerName = match.player2Name ?: "Player 2",
            isWinner = player2IsWinner,
            score = null, // Only show score once, on player 1 row
            colors = colors
        )
    }
}

@Composable
private fun FinalBadge(colors: ThemeColors) {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(6.px)
            marginBottom(12.px)
        }
    }) {
        Span({
            style {
                fontSize(12.px)
                color(colors.textSecondary)
            }
        }) {
            Text("\u2713") // Checkmark
        }
        Span({
            style {
                fontSize(11.px)
                fontWeight(700)
                color(colors.textSecondary)
                textTransform("uppercase")
                letterSpacing(0.5.px)
            }
        }) {
            Text("FINAL")
        }
    }
}

@Composable
private fun CompletedPlayerRow(
    playerName: String,
    isWinner: Boolean,
    score: String?,
    colors: ThemeColors
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.SpaceBetween)
            alignItems(AlignItems.Center)
        }
    }) {
        // Player name with trophy if winner
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(8.px)
                flex(1)
            }
        }) {
            if (isWinner) {
                Span({
                    style {
                        fontSize(14.px)
                    }
                }) {
                    Text("\uD83C\uDFC6") // Trophy emoji
                }
            } else {
                Span({ style { width(22.px) } }) // Spacer to align with trophy
            }
            Span({
                style {
                    fontSize(14.px)
                    fontWeight(if (isWinner) 600 else 400)
                    color(colors.textPrimary)
                    property("white-space", "nowrap")
                    overflow("hidden")
                    property("text-overflow", "ellipsis")
                    maxWidth(180.px)
                }
            }) {
                Text(playerName)
            }
        }

        // Score display (only on first player row)
        score?.let { scoreText ->
            Span({
                style {
                    fontSize(14.px)
                    fontWeight(500)
                    color(colors.textSecondary)
                }
            }) {
                Text(scoreText)
            }
        }
    }
}

@Composable
private fun PlayerScoreRow(
    playerName: String,
    sets: List<Int>,
    games: Int,
    points: String,
    isServing: Boolean,
    fantasyPoints: Double?,
    colors: ThemeColors
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.SpaceBetween)
            alignItems(AlignItems.Center)
        }
    }) {
        // Player name with serving indicator
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(8.px)
                flex(1)
            }
        }) {
            if (isServing) {
                Span({
                    style {
                        width(8.px)
                        height(8.px)
                        borderRadius(50.percent)
                        backgroundColor(colors.secondary)
                    }
                })
            } else {
                Span({ style { width(8.px) } })
            }
            Span({
                style {
                    fontSize(14.px)
                    fontWeight(if (isServing) 600 else 400)
                    color(colors.textPrimary)
                    property("white-space", "nowrap")
                    overflow("hidden")
                    property("text-overflow", "ellipsis")
                    maxWidth(120.px)
                }
            }) {
                Text(playerName)
            }
        }

        // Sets and current game score
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(8.px)
            }
        }) {
            // Set scores
            sets.forEach { setScore ->
                Span({
                    style {
                        width(24.px)
                        textAlign("center")
                        fontSize(14.px)
                        fontWeight(600)
                        color(colors.textPrimary)
                    }
                }) {
                    Text(setScore.toString())
                }
            }

            // Current games in set
            Span({
                style {
                    width(24.px)
                    textAlign("center")
                    fontSize(14.px)
                    fontWeight(500)
                    color(colors.textSecondary)
                    backgroundColor(colors.surfaceVariant)
                    padding(2.px, 6.px)
                    borderRadius(4.px)
                }
            }) {
                Text(games.toString())
            }

            // Current points
            Span({
                style {
                    width(28.px)
                    textAlign("center")
                    fontSize(12.px)
                    color(colors.textSecondary)
                }
            }) {
                Text(points)
            }
        }

        // Fantasy points
        fantasyPoints?.let { pts ->
            Span({
                style {
                    marginLeft(12.px)
                    fontSize(12.px)
                    fontWeight(600)
                    color(colors.primary)
                    backgroundColor(colors.primaryLight)
                    padding(4.px, 8.px)
                    borderRadius(4.px)
                }
            }) {
                Text("${pts.toInt()} FP")
            }
        }
    }
}

// ===== Phase 9: Improved Empty State =====

@Composable
private fun EmptyMatchesState(colors: ThemeColors) {
    Div({
        style {
            textAlign("center")
            padding(64.px, 24.px)
            backgroundColor(colors.surface)
            borderRadius(12.px)
        }
    }) {
        Div({
            style {
                fontSize(48.px)
                marginBottom(16.px)
            }
        }) {
            Text("\uD83C\uDFBE") // Tennis ball emoji
        }

        H3({
            style {
                fontSize(20.px)
                fontWeight(600)
                color(colors.textPrimary)
                marginBottom(8.px)
            }
        }) {
            Text("No Live Matches")
        }

        P({
            style {
                color(colors.textSecondary)
                fontSize(14.px)
                marginBottom(24.px)
                lineHeight("1.5")
            }
        }) {
            Text("There are no matches in progress right now. Check back during tournament play!")
        }

        Div({
            style {
                display(DisplayStyle.Flex)
                gap(12.px)
                justifyContent(JustifyContent.Center)
                flexWrap(FlexWrap.Wrap)
            }
        }) {
            Button(
                text = "Browse Contests",
                onClick = { NavigationState.navigate(Route.Contests) },
                variant = "primary"
            )
            Button(
                text = "View Schedule",
                onClick = { NavigationState.navigate(Route.Matches) },
                variant = "secondary"
            )
        }
    }
}

@Composable
private fun LoadingState(colors: ThemeColors) {
    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(24.px)
        }
    }) {
        // Skeleton for entries section
        SkeletonEntriesSection(colors)

        // Skeleton for matches section
        SkeletonMatchesSection(colors)
    }
}

@Composable
private fun SkeletonEntriesSection(colors: ThemeColors) {
    Section({
        style {
            marginBottom(32.px)
        }
    }) {
        Skeleton(width = 180.px, height = 24.px, borderRadius = 4.px)
        Div({ style { height(16.px) } })

        Div({
            style {
                display(DisplayStyle.Grid)
                property("grid-template-columns", "repeat(auto-fill, minmax(280px, 1fr))")
                gap(16.px)
            }
        }) {
            repeat(2) {
                SkeletonEntryCard(colors)
            }
        }
    }
}

@Composable
private fun SkeletonEntryCard(colors: ThemeColors) {
    Div({
        style {
            backgroundColor(colors.primary)
            borderRadius(12.px)
            padding(20.px)
            property("opacity", "0.5")
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                marginBottom(12.px)
            }
        }) {
            Skeleton(width = 80.px, height = 16.px)
            Skeleton(width = 40.px, height = 24.px, borderRadius = 12.px)
        }
        Skeleton(width = 100.px, height = 32.px)
        Div({ style { height(8.px) } })
        Skeleton(width = 120.px, height = 14.px)
    }
}

@Composable
private fun SkeletonMatchesSection(colors: ThemeColors) {
    Section {
        Skeleton(width = 140.px, height = 24.px, borderRadius = 4.px)
        Div({ style { height(16.px) } })

        Div({
            style {
                display(DisplayStyle.Grid)
                property("grid-template-columns", "repeat(auto-fill, minmax(min(100%, 320px), 1fr))")
                gap(20.px)
            }
        }) {
            repeat(4) {
                SkeletonMatchCard(colors)
            }
        }
    }
}

@Composable
private fun SkeletonMatchCard(colors: ThemeColors) {
    Div({
        style {
            backgroundColor(colors.surface)
            borderRadius(12.px)
            padding(20.px)
            property("box-shadow", "0 2px 8px rgba(0, 0, 0, 0.1)")
        }
    }) {
        // Live badge placeholder
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(6.px)
                marginBottom(12.px)
            }
        }) {
            SkeletonCircle(8.px)
            Skeleton(width = 40.px, height = 12.px)
        }

        // Round/court
        Skeleton(width = 100.px, height = 20.px, borderRadius = 4.px)
        Div({ style { height(12.px) } })

        // Player 1
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
            }
        }) {
            Skeleton(width = 120.px, height = 16.px)
            Skeleton(width = 80.px, height = 16.px)
        }
        Div({ style { height(12.px) } })

        // Divider
        Skeleton(width = 100.percent, height = 1.px)
        Div({ style { height(12.px) } })

        // Player 2
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
            }
        }) {
            Skeleton(width = 110.px, height = 16.px)
            Skeleton(width = 80.px, height = 16.px)
        }
    }
}

@Composable
private fun ErrorState(message: String, colors: ThemeColors, onRetry: () -> Unit) {
    // Extract user-friendly message from raw error
    val displayMessage = when {
        message.contains("OAuth failed") || message.contains("401") ->
            "Live scoring is temporarily unavailable. The scores provider is experiencing issues."
        message.contains("timeout", ignoreCase = true) ->
            "Request timed out. Please check your connection and try again."
        message.contains("network", ignoreCase = true) ->
            "Network error. Please check your internet connection."
        message.contains("500") ->
            "Server error. Our team has been notified."
        else -> message.substringBefore("JSON input:").trim().ifEmpty { "Unable to load live matches" }
    }

    Div({
        style {
            textAlign("center")
            padding(48.px)
            backgroundColor(colors.surface)
            borderRadius(12.px)
            maxWidth(500.px)
            margin(0.px, "auto".unsafeCast<CSSNumeric>())
        }
    }) {
        Div({
            style {
                fontSize(48.px)
                marginBottom(16.px)
            }
        }) {
            Text("\u26A0\uFE0F") // Warning emoji
        }

        H3({
            style {
                fontSize(18.px)
                fontWeight(600)
                color(colors.textPrimary)
                marginBottom(8.px)
            }
        }) {
            Text("Unable to Load Live Scores")
        }

        P({
            style {
                color(colors.textSecondary)
                fontSize(14.px)
                marginBottom(24.px)
                lineHeight("1.5")
            }
        }) {
            Text(displayMessage)
        }

        Div({
            style {
                display(DisplayStyle.Flex)
                gap(12.px)
                justifyContent(JustifyContent.Center)
            }
        }) {
            Button(text = "Retry", onClick = onRetry, variant = "primary")
            Button(
                text = "Back to Home",
                onClick = { NavigationState.navigate(Route.Home) },
                variant = "secondary"
            )
        }
    }
}

// Helper function for rgba color
private fun rgba(r: Int, g: Int, b: Int, a: Double): CSSColorValue {
    return Color("rgba($r, $g, $b, $a)")
}

/**
 * Extract games won in each completed set for a player from periodScores.
 * For live matches, the last period represents the current set in progress,
 * so we exclude it from the "completed sets" display.
 */
private fun extractCompletedSetScores(
    periodScores: List<PeriodScoreEntity>,
    isPlayer1: Boolean
): List<Int> {
    if (periodScores.isEmpty()) return emptyList()
    // All but the last period are completed sets
    return periodScores.dropLast(1).map {
        if (isPlayer1) it.homeScore else it.awayScore
    }
}

/**
 * Get games in the current set (from the last period in periodScores).
 */
private fun getCurrentSetGames(
    periodScores: List<PeriodScoreEntity>,
    isPlayer1: Boolean
): Int {
    val lastPeriod = periodScores.lastOrNull() ?: return 0
    return if (isPlayer1) lastPeriod.homeScore else lastPeriod.awayScore
}
